import { signInWithPopup, signOut as firebaseSignOut } from 'firebase/auth'
import { auth, googleProvider, persistenceReady } from '../config/firebase'
import { ApiError, clearSession, get, post, readRefreshToken, writeSession } from './client'
import { clearPlaces } from '../utils/places'
import { isInAppBrowser } from '../utils/inAppBrowser'

/**
 * The account half of the API.
 *
 * The sequence is the same in all four apps:
 *   Google popup → ID token → POST /api/auth/google → our JWT pair → localStorage.
 */

/** Popup outcomes that are the customer changing their mind, not a failure to report. */
const CANCELLED = new Set([
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request',
  'auth/user-cancelled',
])

export class SignInCancelled extends Error {
  constructor() {
    super('Sign-in was cancelled.')
    this.name = 'SignInCancelled'
  }
}

/**
 * The sign-in helper page could not read the state it had just written.
 *
 * Firebase words this as "missing initial state", which tells a customer nothing. It means
 * the browser refused the storage the sign-in flow runs on. Serving that helper page from
 * our own domain — the proxy in `public/_redirects` — removes the usual cause, third-party
 * storage partitioning. What can still reach here is a browser with storage switched off
 * outright, or private-mode rules stricter than the proxy can help with.
 */
const STORAGE_BLOCKED = new Set([
  'auth/missing-initial-state',
  'auth/web-storage-unsupported',
  'auth/operation-not-supported-in-this-environment',
])

/**
 * The detection moved to `utils/inAppBrowser.js`, and this file is now one of two readers.
 *
 * It was here first and it was private, which meant the app could only act on it at the
 * moment sign-in had already failed — at the end of a form somebody had filled in. The
 * strip at the top of every screen (`components/common/OpenInBrowser`) says the same thing
 * before any of that, and both have to agree about which browsers are the problem.
 *
 * This remains the last word rather than the only one: an in-app browser reports the
 * failure under several different Firebase codes, and every one of them has this answer.
 */

/**
 * `client` decides the session's lifetime — 1h access, 30d refresh for a customer.
 * Omitted, the server falls back to this same value, but it is sent explicitly so the
 * choice is visible here rather than inherited from a default that could change.
 *
 * Unlike `partner`, `rider` and `admin`, this one is never refused: signing in with
 * Google grants the `user` role, so every account that can sign in at all can sign in
 * here. There is no wrong-account screen in this app because there is no such thing.
 */
const CLIENT = 'user'

export async function signInWithGoogle() {
  let credential

  await persistenceReady

  try {
    credential = await signInWithPopup(auth, googleProvider)
  } catch (error) {
    if (CANCELLED.has(error.code)) throw new SignInCancelled()

    if (error.code === 'auth/popup-blocked') {
      throw new ApiError('Your browser blocked the sign-in window. Allow popups for this site and try again.', 0)
    }

    if (error.code === 'auth/network-request-failed') {
      throw new ApiError('Could not reach Google to sign in. Check your connection and try again.', 0)
    }

    // Checked before the error code, not after: inside Messenger or Instagram the failure
    // surfaces under several different codes, and every one of them has the same answer.
    if (isInAppBrowser()) {
      throw new ApiError(
        'Google sign-in does not work inside this app’s browser. Tap the menu (⋯) and choose “Open in browser”, then sign in there.',
        0,
      )
    }

    if (STORAGE_BLOCKED.has(error.code)) {
      throw new ApiError(
        'Your browser is blocking the storage sign-in needs. Turn off private browsing or allow cookies for this site, then try again.',
        0,
      )
    }

    throw new ApiError('Google sign-in did not complete. Try again.', 0)
  }

  const idToken = await credential.user.getIdToken()

  try {
    const { data } = await post('/api/auth/google', { idToken, client: CLIENT })

    writeSession(data.accessToken, data.refreshToken)

    return data.user
  } finally {
    // The ID token has been spent. Firebase has done its one job, and holding a session
    // it no longer needs only creates a second source of truth about who is signed in.
    await firebaseSignOut(auth).catch(() => {})
  }
}

/** The signed-in account, or null. Used to restore a session on reload. */
export async function fetchCurrentUser() {
  const { data } = await get('/api/auth/me')

  return data
}

/**
 * Sign out server-side, then locally.
 *
 * The refresh token goes in the body so the server can revoke that specific session
 * rather than only forgetting it here — a token left alive in the database is a token
 * that still works. Local state is cleared even if the call fails, because a sign-out
 * that leaves the customer signed in is worse than one the server did not hear about.
 */
export async function signOut() {
  const refreshToken = readRefreshToken()

  try {
    if (refreshToken) await post('/api/auth/signout', { refreshToken })
  } catch {
    // Already expired, revoked, or the server is down. Nothing useful to say.
  } finally {
    clearSession()

    /**
     * The house and the number go with the session.
     *
     * `places.js` holds where this customer lives and the shops they buy from, and the
     * handset it is written on is borrowed as often as it is owned. Signing out is the act
     * of handing the phone back.
     *
     * Here and not in `clearSession`, which also runs when a token simply lapses. An
     * expired session is the same person a moment later; wiping their address because their
     * JWT aged out would cost them the map on their next order for no gain at all.
     *
     * The gap that leaves is narrow and real: expire, then a *different* person signs in on
     * the same handset without the first ever pressing sign out. Closing it needs the store
     * bound to an account, which is the thing this feature deliberately does not do.
     */
    clearPlaces()
  }
}
